import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import {
  fetchGuarderias,
  createGuarderia,
  registrarPagoGuarderia,
  fetchPagosGuarderia,
  fetchClientesGuarderia,
} from '../services/guarderiaService';
import { Card, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  BellRing,
  CalendarClock,
  DollarSign,
  Download,
  FileText,
  History,
  Loader2,
  Warehouse,
  X,
} from 'lucide-react';

const DEFAULT_PAYMENT_FORM = {
  periodInput: '',
  periodos: [],
  fechaPago: '',
  monto: '',
  montoEsPersonalizado: false,
  metodoPago: '',
  observaciones: '',
  comprobante: null,
};

const METODOS_PAGO = ['Efectivo', 'Transferencia', 'Tarjeta', 'Débito automático'];

const monthFormatter = new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric' });

const formatPeriodoHumano = (periodo) => {
  if (!periodo) return '—';
  const [year, month] = periodo.split('-').map(Number);
  if (!year || !month) return periodo;
  return monthFormatter.format(new Date(year, month - 1, 1));
};

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  if (Number.isNaN(numeric)) return '$0';
  return numeric.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
};

const getPeriodoFromDateString = (value) => {
  if (!value) return '';
  const normalized = new Date(value);
  if (Number.isNaN(normalized.getTime())) return '';
  return `${normalized.getFullYear()}-${`${normalized.getMonth() + 1}`.padStart(2, '0')}`;
};

const parseComprobante = (value) => {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return { nombre: 'Comprobante', data: value };
  }
};

const isPeriodoVencido = (periodo) => {
  if (!periodo) return false;
  if (periodo.estado === 'vencido' || periodo.estadoVencimiento === 'vencido' || periodo.estaVencido) {
    return true;
  }
  if (!periodo.fechaVencimiento) return false;
  const fechaVencimiento = new Date(periodo.fechaVencimiento);
  if (Number.isNaN(fechaVencimiento.getTime())) {
    return false;
  }
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fechaComparacion = new Date(fechaVencimiento);
  fechaComparacion.setHours(0, 0, 0, 0);
  return fechaComparacion < hoy;
};

const estadoStyles = {
  vencido: 'bg-red-600/15 text-red-700 border-red-500/40',
  proximo: 'bg-amber-500/20 text-amber-800 border-amber-500/40',
  al_dia: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/40',
  sin_datos: 'bg-slate-200 text-slate-700 border-slate-300',
};

const estadoLabels = {
  vencido: 'Vencido',
  proximo: 'Próximo a vencer',
  al_dia: 'Al día',
  sin_datos: 'Sin datos',
};

const Guarderia = () => {
  const [guarderias, setGuarderias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedBoatId, setSelectedBoatId] = useState('');
  const [createForm, setCreateForm] = useState({ cuotaMensual: '', fechaInicio: '', notas: '' });
  const [selectedGuarderia, setSelectedGuarderia] = useState(null);
  const [pagosGuarderia, setPagosGuarderia] = useState([]);
  const [paymentForm, setPaymentForm] = useState(DEFAULT_PAYMENT_FORM);
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const tableSectionRef = useRef(null);

  const recalculateMonto = useCallback(
    (nextPeriodos, currentForm) => {
      if (currentForm.montoEsPersonalizado) {
        return currentForm.monto;
      }
      const cuota = selectedGuarderia?.cuotaMensual ? Number(selectedGuarderia.cuotaMensual) : null;
      if (!cuota) {
        return currentForm.monto;
      }
      if (nextPeriodos.length === 0) {
        return cuota.toFixed(2);
      }
      return (cuota * nextPeriodos.length).toFixed(2);
    },
    [selectedGuarderia?.cuotaMensual]
  );

  const normalizeDecimalInput = (value) => {
    if (value === '') return '';
    const normalized = value.replace(/,/g, '.');
    const preparedValue = normalized.startsWith('.') ? `0${normalized}` : normalized;
    if (/^\d*(\.\d{0,2})?$/.test(preparedValue)) {
      return preparedValue;
    }
    return null;
  };

  const handleCuotaMensualChange = (event) => {
    const nextValue = normalizeDecimalInput(event.target.value);
    if (nextValue !== null) {
      setCreateForm((prev) => ({ ...prev, cuotaMensual: nextValue }));
    }
  };

  const handleMontoPagoChange = (event) => {
    const nextValue = normalizeDecimalInput(event.target.value);
    if (nextValue !== null) {
      setPaymentForm((prev) => ({ ...prev, monto: nextValue, montoEsPersonalizado: true }));
    }
  };

  const loadGuarderias = async (term = '') => {
    try {
      setLoading(true);
      const data = await fetchGuarderias(term.trim() ? term : undefined);
      setGuarderias(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error cargando guarderías:', err);
      setError('No se pudieron cargar las guarderías.');
      setGuarderias([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      loadGuarderias(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    loadGuarderias('');
  }, []);

  const fetchClientes = useCallback(async () => {
    try {
      const data = await fetchClientesGuarderia();
      setClientes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('No se pudieron cargar los clientes para la guardería:', err);
      toast.error('No se pudieron cargar los clientes para la guardería');
    }
  }, []);

  const selectedClient = useMemo(
    () => clientes.find((cliente) => String(cliente.id) === String(selectedClientId)),
    [clientes, selectedClientId]
  );

  const filteredBoats = useMemo(() => {
    if (!selectedClient) return [];
    return selectedClient.embarcaciones || [];
  }, [selectedClient]);

  const filteredGuarderias = useMemo(() => {
    if (!Array.isArray(guarderias)) return [];
    if (statusFilter === 'all') return guarderias;
    if (statusFilter === 'deuda') {
      return guarderias.filter((item) => item.estadoVencimiento === 'vencido');
    }
    if (statusFilter === 'proximo') {
      return guarderias.filter((item) => item.estadoVencimiento === 'proximo');
    }
    if (statusFilter === 'al_dia') {
      return guarderias.filter((item) => item.estadoVencimiento === 'al_dia');
    }
    return guarderias;
  }, [guarderias, statusFilter]);

  const resumen = useMemo(() => {
    if (!Array.isArray(guarderias)) {
      return { activas: 0, total: 0, mensual: 0, deuda: 0 };
    }
    const activas = guarderias.filter((item) => item.estado === 'Activo').length;
    const total = guarderias.length;
    const mensual = guarderias.reduce((acc, item) => acc + Number(item.cuotaMensual || 0), 0);
    const deuda = guarderias.filter((item) => item.estadoVencimiento === 'vencido').length;
    return { activas, total, mensual, deuda };
  }, [guarderias]);

  const totalPagado = useMemo(
    () => pagosGuarderia.reduce((acc, pago) => acc + Number(pago.monto || 0), 0),
    [pagosGuarderia]
  );

  const montoSugerido = useMemo(() => {
    if (!selectedGuarderia?.cuotaMensual) return null;
    if (!paymentForm.periodos.length) return Number(selectedGuarderia.cuotaMensual);
    return Number(selectedGuarderia.cuotaMensual) * paymentForm.periodos.length;
  }, [paymentForm.periodos, selectedGuarderia]);

  const handleOpenCreate = useCallback(
    async (open) => {
      setIsCreateOpen(open);
      if (open && clientes.length === 0) {
        await fetchClientes();
      }
      if (!open) {
        setSelectedClientId('');
        setSelectedBoatId('');
        setCreateForm({ cuotaMensual: '', fechaInicio: '', notas: '' });
      }
    },
    [clientes.length, fetchClientes]
  );

  const handleCreateGuarderia = async (event) => {
    event.preventDefault();
    if (
      !selectedClientId ||
      !selectedBoatId ||
      !createForm.cuotaMensual ||
      Number.isNaN(Number(createForm.cuotaMensual)) ||
      !createForm.fechaInicio
    ) {
      toast.error('Completá todos los campos obligatorios');
      return;
    }

    try {
      setSubmittingCreate(true);
      await createGuarderia({
        cliente_id: Number(selectedClientId),
        embarcacion_id: Number(selectedBoatId),
        cuotaMensual: Number(createForm.cuotaMensual),
        fechaInicio: createForm.fechaInicio,
        notas: createForm.notas || undefined,
      });
      toast.success('Guardería creada correctamente');
      await loadGuarderias(searchTerm);
      handleOpenCreate(false);
    } catch (err) {
      console.error('Error al crear guardería:', err);
      const message = err?.response?.data?.error || 'No se pudo crear la guardería';
      toast.error(message);
    } finally {
      setSubmittingCreate(false);
    }
  };

  const handleOpenHistory = async (guarderia) => {
    setSelectedGuarderia(guarderia);
    setIsHistoryOpen(true);
    try {
      const detalle = await fetchPagosGuarderia(guarderia.id);
      if (detalle) {
        setSelectedGuarderia(detalle);
        setPagosGuarderia(Array.isArray(detalle.pagos) ? detalle.pagos : []);
      } else {
        setPagosGuarderia([]);
      }
    } catch (err) {
      console.error('Error al obtener historial de pagos:', err);
      toast.error('No se pudo cargar el historial de pagos');
      setPagosGuarderia([]);
    }
  };

  const handleCloseHistory = useCallback(() => {
    setPagosGuarderia([]);
    setSelectedGuarderia(null);
    setIsHistoryOpen(false);
  }, []);

  const handleHistoryDialogChange = useCallback(
    (open) => {
      if (!open) {
        handleCloseHistory();
      }
    },
    [handleCloseHistory]
  );

  const handleOpenPayment = (guarderia) => {
    const periodosPendientes = guarderia?.periodosPendientes?.map((periodo) => periodo.periodo) || [];
    const proximoPeriodo = guarderia?.proximoVencimiento
      ? getPeriodoFromDateString(guarderia.proximoVencimiento)
      : '';
    const periodosIniciales = periodosPendientes.length > 0
      ? periodosPendientes
      : proximoPeriodo
        ? [proximoPeriodo]
        : [];
    const cuota = guarderia?.cuotaMensual ? Number(guarderia.cuotaMensual) : 0;
    const montoInicial = cuota && periodosIniciales.length > 0
      ? (cuota * periodosIniciales.length).toFixed(2)
      : cuota
        ? cuota.toFixed(2)
        : '';

    setSelectedGuarderia(guarderia);
    setPaymentForm({
      ...DEFAULT_PAYMENT_FORM,
      periodos: periodosIniciales,
      monto: montoInicial,
      montoEsPersonalizado: false,
    });
    setIsPaymentOpen(true);
  };

  const handleRegisterPayment = async (event) => {
    event.preventDefault();
    if (!selectedGuarderia) return;

    if (paymentForm.periodos.length === 0) {
      toast.error('Agregá al menos un periodo de pago');
      return;
    }

    try {
      setSubmittingPayment(true);
      await registrarPagoGuarderia(selectedGuarderia.id, {
        periodos: paymentForm.periodos,
        fechaPago: paymentForm.fechaPago || undefined,
        monto: paymentForm.monto ? Number(paymentForm.monto) : undefined,
        metodoPago: paymentForm.metodoPago || undefined,
        observaciones: paymentForm.observaciones || undefined,
        comprobante: paymentForm.comprobante ? JSON.stringify(paymentForm.comprobante) : undefined,
      });
      toast.success('Pago registrado correctamente');
      await loadGuarderias(searchTerm);
      setIsPaymentOpen(false);
      setPaymentForm(DEFAULT_PAYMENT_FORM);
    } catch (err) {
      console.error('Error al registrar pago de guardería:', err);
      const message = err?.response?.data?.error || 'No se pudo registrar el pago';
      toast.error(message);
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleClosePayment = useCallback(() => {
    setIsPaymentOpen(false);
    setPaymentForm(DEFAULT_PAYMENT_FORM);
    setSelectedGuarderia(null);
  }, []);

  const handlePaymentDialogChange = useCallback(
    (open) => {
      if (!open) {
        handleClosePayment();
      }
    },
    [handleClosePayment]
  );

  const handleAddPeriodo = () => {
    if (!paymentForm.periodInput) {
      toast.error('Seleccioná un periodo en formato YYYY-MM');
      return;
    }
    if (paymentForm.periodos.includes(paymentForm.periodInput)) {
      toast.error('Ese periodo ya fue agregado');
      return;
    }

    setPaymentForm((prev) => {
      const nextPeriodos = [...prev.periodos, prev.periodInput];
      const nextMonto = recalculateMonto(nextPeriodos, prev);
      return {
        ...prev,
        periodos: nextPeriodos,
        periodInput: '',
        monto: nextMonto,
      };
    });
  };

  const handleRemovePeriodo = (periodo) => {
    setPaymentForm((prev) => {
      const nextPeriodos = prev.periodos.filter((item) => item !== periodo);
      const nextMonto = recalculateMonto(nextPeriodos, prev);
      return {
        ...prev,
        periodos: nextPeriodos,
        monto: nextMonto,
      };
    });
  };

  const handleTogglePeriodoPendiente = useCallback(
    (periodoId) => {
      if (!periodoId) return;
      setPaymentForm((prev) => {
        const isSelected = prev.periodos.includes(periodoId);
        const nextPeriodos = isSelected
          ? prev.periodos.filter((item) => item !== periodoId)
          : [...prev.periodos, periodoId];
        const nextMonto = recalculateMonto(nextPeriodos, prev);
        return {
          ...prev,
          periodos: nextPeriodos,
          monto: nextMonto,
        };
      });
    },
    [recalculateMonto]
  );

  const handleComprobanteUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setPaymentForm((prev) => ({ ...prev, comprobante: null }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPaymentForm((prev) => ({
        ...prev,
        comprobante: {
          nombre: file.name,
          tipo: file.type,
          data: reader.result,
        },
      }));
    };
    reader.readAsDataURL(file);
  };

  const clearComprobante = () => {
    setPaymentForm((prev) => ({ ...prev, comprobante: null }));
  };

  const handleQuickFilterChange = (nextFilter) => {
    setStatusFilter(nextFilter);
    if (tableSectionRef.current) {
      tableSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDownloadResumen = () => {
    if (!selectedGuarderia) return;
    const rows = [];
    rows.push(['Titular', `${selectedGuarderia?.cliente?.nombre || ''} ${selectedGuarderia?.cliente?.apellido || ''}`.trim()]);
    rows.push(['Matrícula', selectedGuarderia?.embarcacion?.numeroMatricula || '']);
    rows.push(['Embarcación', selectedGuarderia?.embarcacion?.nombreFantasia || '']);
    rows.push(['Estado', estadoLabels[selectedGuarderia?.estadoVencimiento] || '']);
    rows.push(['Meses adeudados', selectedGuarderia?.resumenCuenta?.mesesAdeudados ?? 0]);
    rows.push(['Monto adeudado', formatCurrency(selectedGuarderia?.resumenCuenta?.montoAdeudado || 0)]);
    rows.push(['Intereses', formatCurrency(selectedGuarderia?.resumenCuenta?.montoInteres || 0)]);
    rows.push(['Total adeudado', formatCurrency(selectedGuarderia?.resumenCuenta?.totalAdeudado || 0)]);
    rows.push(['Total pagado', formatCurrency(totalPagado)]);
    rows.push([]);
    rows.push(['Pagos registrados']);
    rows.push(['Periodo', 'Fecha de pago', 'Monto', 'Método', 'Estado']);
    pagosGuarderia.forEach((pago) => {
      rows.push([
        formatPeriodoHumano(pago.periodo),
        pago.fechaPago ? new Date(pago.fechaPago).toLocaleDateString('es-AR') : '',
        formatCurrency(pago.monto),
        pago.metodoPago || '',
        pago.estado || 'Pagado',
      ]);
    });
    rows.push([]);
    if (selectedGuarderia?.periodosPendientes?.length) {
      rows.push(['Periodos pendientes']);
      rows.push(['Periodo', 'Fecha de vencimiento', 'Monto estimado']);
      selectedGuarderia.periodosPendientes.forEach((periodo) => {
        rows.push([
          formatPeriodoHumano(periodo.periodo),
          periodo.fechaVencimiento ? new Date(periodo.fechaVencimiento).toLocaleDateString('es-AR') : '',
          formatCurrency(periodo.monto),
        ]);
      });
    }

    const csvContent = rows
      .map((row) =>
        row
          .map((value) => {
            const cell = value === null || value === undefined ? '' : String(value);
            return `"${cell.replace(/"/g, '""')}"`;
          })
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const fileNameBase = selectedGuarderia?.embarcacion?.numeroMatricula || 'guarderia';
    link.setAttribute('href', url);
    link.setAttribute('download', `resumen-guarderia-${fileNameBase}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderEstadoBadge = (guarderia) => {
    const estado = guarderia?.estadoVencimiento || 'sin_datos';
    const style = estadoStyles[estado] || estadoStyles.sin_datos;
    return (
      <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold capitalize ${style}`}>
        {estadoLabels[estado]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blanco-puro via-white to-slate-100">
      <Navbar />
      <main className="container mx-auto px-4 py-8 space-y-8">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-azul-oscuro flex items-center gap-2">
              <Warehouse className="h-8 w-8 text-verde-esmeralda" /> Guardería de embarcaciones
            </h1>
            <p className="text-slate-700 mt-1 max-w-2xl">
              Gestioná las embarcaciones guardadas, controlá los vencimientos mensuales y accedé al historial de pagos en un solo lugar.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Dialog open={isCreateOpen} onOpenChange={handleOpenCreate}>
              <DialogTrigger asChild>
                <Button className="bg-verde-esmeralda hover:bg-verde-esmeralda/90 text-white">
                  Agregar embarcación a guardería
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                  <DialogTitle>Registrar nueva guardería</DialogTitle>
                  <p className="text-sm text-slate-600">
                    Seleccioná un cliente y la embarcación asociada. La cuota mensual se utilizará para calcular los pagos futuros.
                  </p>
                </DialogHeader>
                <form className="space-y-4" onSubmit={handleCreateGuarderia}>
                  <div>
                    <label className="block text-sm font-medium text-azul-oscuro mb-2">Cliente titular</label>
                    <Select
                      value={selectedClientId}
                      onValueChange={(value) => {
                        setSelectedClientId(value);
                        setSelectedBoatId('');
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccioná un cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientes.map((cliente) => (
                          <SelectItem key={cliente.id} value={String(cliente.id)}>
                            <div className="flex flex-col text-left">
                              <span className="font-medium text-azul-oscuro">
                                {cliente.nombre} {cliente.apellido}
                              </span>
                              <span className="text-xs text-slate-600">
                                DNI {cliente.dni}
                                {cliente.telefono1 && ` · Tel: ${cliente.telefono1}`}
                                {!cliente.telefono1 && cliente.telefono2 && ` · Tel: ${cliente.telefono2}`}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedClient && (
                      <div className="mt-3 rounded-md border border-azul-oscuro/10 bg-white p-3 text-sm shadow-sm">
                        <p className="font-semibold text-azul-oscuro">
                          {selectedClient.nombre} {selectedClient.apellido}
                        </p>
                        <p className="text-slate-600">
                          DNI: {selectedClient.dni}
                          {selectedClient.domicilio && ` · Domicilio: ${selectedClient.domicilio}`}
                        </p>
                        {(selectedClient.telefono1 || selectedClient.telefono2) && (
                          <p className="text-slate-600">
                            Teléfono: {selectedClient.telefono1 || selectedClient.telefono2}
                            {selectedClient.telefono1 && selectedClient.telefono2
                              ? ` · Alternativo: ${selectedClient.telefono2}`
                              : ''}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-azul-oscuro mb-2">Embarcación</label>
                    <Select
                      value={selectedBoatId}
                      onValueChange={setSelectedBoatId}
                      disabled={!selectedClientId || filteredBoats.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            selectedClientId ? 'Seleccioná una embarcación' : 'Elegí un cliente primero'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredBoats.map((boat) => (
                          <SelectItem key={boat.id} value={String(boat.id)}>
                            {boat.numeroMatricula} · {boat.nombreFantasia || 'N/A'}
                            {boat.tipoEmbarcacion ? ` (${boat.tipoEmbarcacion})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedClientId && filteredBoats.length === 0 && (
                      <p className="text-sm text-amarillo-dorado mt-1">
                        El cliente seleccionado no tiene embarcaciones registradas.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-azul-oscuro mb-2">Cuota mensual</label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={createForm.cuotaMensual}
                        onChange={handleCuotaMensualChange}
                        placeholder="Ej: 75000"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-azul-oscuro mb-2">Fecha de inicio</label>
                      <Input
                        type="date"
                        value={createForm.fechaInicio}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, fechaInicio: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-azul-oscuro mb-2">Notas</label>
                    <textarea
                      className="w-full rounded-md border border-azul-oscuro/20 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-verde-esmeralda"
                      rows={3}
                      value={createForm.notas}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, notas: e.target.value }))}
                      placeholder="Observaciones adicionales"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => handleOpenCreate(false)} disabled={submittingCreate}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-verde-esmeralda text-white" disabled={submittingCreate}>
                      {submittingCreate ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <section className="grid gap-4 sm:grid-cols-1 md:grid-cols-4 mb-8">
          <Card className="bg-white/95 shadow-lg border border-azul-oscuro/10">
            <CardHeader className="border-none pb-0">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-azul-oscuro/80">Guarderías activas</CardTitle>
              <CardDescription className="text-3xl font-bold text-azul-oscuro">{resumen.activas}</CardDescription>
            </CardHeader>
          </Card>
          <Card className="bg-white/95 shadow-lg border border-azul-oscuro/10">
            <CardHeader className="border-none pb-0">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-azul-oscuro/80">Total de embarcaciones</CardTitle>
              <CardDescription className="text-3xl font-bold text-azul-oscuro">{resumen.total}</CardDescription>
            </CardHeader>
          </Card>
          <Card className="bg-white/95 shadow-lg border border-azul-oscuro/10">
            <CardHeader className="flex flex-row items-center justify-between border-none pb-0">
              <div>
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-azul-oscuro/80">Ingresos mensuales estimados</CardTitle>
                <CardDescription className="text-3xl font-bold text-azul-oscuro">
                  {formatCurrency(resumen.mensual)}
                </CardDescription>
              </div>
              <DollarSign className="h-8 w-8 text-verde-esmeralda" />
            </CardHeader>
          </Card>
          <Card
            className={`bg-white/95 shadow-lg transition border border-azul-oscuro/10 ${
              statusFilter === 'deuda' ? 'border-red-500 shadow-xl ring-1 ring-red-500/30' : ''
            } cursor-pointer`}
            onClick={() => handleQuickFilterChange('deuda')}
          >
            <CardHeader className="flex flex-row items-center justify-between border-none pb-0">
              <div>
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-azul-oscuro/80">Embarcaciones con deuda</CardTitle>
                <CardDescription className="text-3xl font-bold text-red-600">
                  {resumen.deuda}
                </CardDescription>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </CardHeader>
          </Card>
        </section>

        <section ref={tableSectionRef} className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex-1">
              <Input
                placeholder="Buscar por titular, matrícula, domicilio o celular"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-azul-oscuro">Filtros rápidos:</span>
              <Button
                size="sm"
                variant={statusFilter === 'all' ? 'secondary' : 'outline'}
                onClick={() => handleQuickFilterChange('all')}
              >
                Todas
              </Button>
              <Button
                size="sm"
                variant={statusFilter === 'deuda' ? 'secondary' : 'outline'}
                onClick={() => handleQuickFilterChange('deuda')}
                className="flex items-center gap-1"
              >
                <AlertTriangle className="h-4 w-4" /> Con deuda
              </Button>
              <Button
                size="sm"
                variant={statusFilter === 'proximo' ? 'secondary' : 'outline'}
                onClick={() => handleQuickFilterChange('proximo')}
                className="flex items-center gap-1"
              >
                <BellRing className="h-4 w-4" /> Próximas 2 semanas
              </Button>
              <Button
                size="sm"
                variant={statusFilter === 'al_dia' ? 'secondary' : 'outline'}
                onClick={() => handleQuickFilterChange('al_dia')}
              >
                Al día
              </Button>
            </div>
          </div>

          {statusFilter !== 'all' && (
            <div className="mb-4">
              <Badge variant="outline" className="text-sm border-azul-oscuro/30 text-azul-oscuro">
                Filtro aplicado: {statusFilter === 'deuda' && 'Con deuda'}
                {statusFilter === 'proximo' && 'Próximas a vencer'}
                {statusFilter === 'al_dia' && 'Al día'}
              </Badge>
            </div>
          )}

          {error && (
            <div className="text-red-500 text-sm mb-4">{error}</div>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-verde-esmeralda" />
            </div>
          ) : filteredGuarderias.length === 0 ? (
            <div className="text-center py-16 text-slate-600">
              No hay embarcaciones registradas con los criterios actuales.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table id="tabla-guarderia">
                <TableHeader>
                  <TableRow>
                    <TableHead>Titular</TableHead>
                    <TableHead>Matrícula</TableHead>
                    <TableHead>Embarcación</TableHead>
                    <TableHead>Identificación</TableHead>
                    <TableHead>Celular</TableHead>
                    <TableHead>Cuota mensual</TableHead>
                    <TableHead>Próximo vencimiento</TableHead>
                    <TableHead>Último pago</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGuarderias.map((item) => {
                    const estado = item.estadoVencimiento || 'sin_datos';
                    const ultimoPago = item?.ultimoPago;
                    const cuotaFormateada = formatCurrency(item.cuotaMensual || 0);
                    const proximoLabel = item.proximoVencimiento
                      ? new Date(item.proximoVencimiento).toLocaleDateString('es-AR')
                      : 'Sin datos';
                    const diasParaVencimiento = item?.resumenCuenta?.diasParaVencimiento;
                    const indicadorEstado = renderEstadoBadge(item);

                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <span>
                                {item?.cliente?.nombre} {item?.cliente?.apellido}
                              </span>
                              {estado === 'vencido' && <AlertTriangle className="h-4 w-4 text-red-600" aria-label="Cuenta con deuda" />}
                              {estado === 'proximo' && <BellRing className="h-4 w-4 text-amarillo-dorado" aria-label="Pago próximo a vencer" />}
                            </div>
                            {indicadorEstado}
                          </div>
                        </TableCell>
                        <TableCell>{item?.embarcacion?.numeroMatricula || 'Sin matrícula'}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{item?.embarcacion?.nombreFantasia || 'N/A'}</span>
                            {item?.embarcacion?.tipoEmbarcacion && (
                              <span className="text-xs text-slate-600">{item.embarcacion.tipoEmbarcacion}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-azul-oscuro">{item?.cliente?.dni || 'N/A'}</span>
                            {item?.cliente?.domicilio && (
                              <span className="text-xs text-slate-600">{item.cliente.domicilio}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{item?.cliente?.telefono1 || item?.cliente?.telefono2 || 'Sin datos'}</span>
                            {item?.cliente?.telefono1 && item?.cliente?.telefono2 && (
                              <span className="text-xs text-slate-600">Alt: {item.cliente.telefono2}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{cuotaFormateada}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span
                              className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm font-semibold ${
                                estadoStyles[estado] || estadoStyles.sin_datos
                              }`}
                            >
                              {estado === 'vencido' ? 'VENCIDO' : proximoLabel}
                            </span>
                            {typeof diasParaVencimiento === 'number' && estado !== 'vencido' && (
                              <span className="text-xs text-slate-600">
                                {diasParaVencimiento > 0
                                  ? `${diasParaVencimiento} días restantes`
                                  : 'Vence hoy'}
                              </span>
                            )}
                            {estado === 'vencido' && item?.resumenCuenta?.mesesAdeudados > 0 && (
                              <span className="text-xs text-red-600 font-semibold">
                                {item.resumenCuenta.mesesAdeudados} mes(es) adeudado(s)
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {ultimoPago ? (
                            <div className="flex flex-col">
                              <span className="font-medium">{formatPeriodoHumano(ultimoPago.periodo)}</span>
                              <span className="text-xs text-slate-600">
                                {ultimoPago.fechaPago
                                  ? new Date(ultimoPago.fechaPago).toLocaleDateString('es-AR')
                                  : ''}
                              </span>
                            </div>
                          ) : (
                            'Sin pagos registrados'
                          )}
                        </TableCell>
                        <TableCell className="space-x-2 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenHistory(item)}
                            className="inline-flex items-center gap-1"
                          >
                            <History className="h-4 w-4" /> Historial
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleOpenPayment(item)}
                            className="inline-flex items-center gap-1"
                          >
                            Registrar pago
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </main>

      <Dialog open={isHistoryOpen} onOpenChange={handleHistoryDialogChange}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Historial de pagos</DialogTitle>
            <p className="text-sm text-slate-600">
              {selectedGuarderia
                ? `${selectedGuarderia?.cliente?.nombre || ''} ${selectedGuarderia?.cliente?.apellido || ''} · ${selectedGuarderia?.embarcacion?.numeroMatricula || ''}`
                : 'Pagos registrados'}
            </p>
          </DialogHeader>
          {selectedGuarderia && (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-azul-oscuro/15 bg-white p-4 shadow-sm">
                  <p className="text-xs uppercase text-azul-oscuro/70 font-semibold">Estado</p>
                  <div className="mt-2">{renderEstadoBadge(selectedGuarderia)}</div>
                </div>
                <div className="rounded-lg border border-azul-oscuro/15 bg-white p-4 shadow-sm">
                  <p className="text-xs uppercase text-azul-oscuro/70 font-semibold">Meses adeudados</p>
                  <p className="mt-2 text-2xl font-bold text-azul-oscuro">
                    {selectedGuarderia?.resumenCuenta?.mesesAdeudados ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-azul-oscuro/15 bg-white p-4 shadow-sm">
                  <p className="text-xs uppercase text-azul-oscuro/70 font-semibold">Total adeudado</p>
                  <p className="mt-2 text-2xl font-bold text-azul-oscuro">
                    {formatCurrency(selectedGuarderia?.resumenCuenta?.totalAdeudado || 0)}
                  </p>
                </div>
              </div>
              {selectedGuarderia?.resumenCuenta?.montoInteres > 0 && (
                <div className="rounded-md border border-amarillo-dorado/60 bg-amarillo-dorado/10 p-3 text-sm text-azul-oscuro">
                  Incluye intereses por mora: {formatCurrency(selectedGuarderia.resumenCuenta.montoInteres)}
                </div>
              )}
            </div>
          )}

          {pagosGuarderia.length === 0 ? (
            <p className="text-slate-600">No hay pagos registrados para esta guardería.</p>
          ) : (
            <div className="overflow-x-auto max-h-80">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Periodo</TableHead>
                    <TableHead>Fecha de pago</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Comprobante</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagosGuarderia.map((pago) => {
                    const comprobante = parseComprobante(pago.comprobante);
                    return (
                      <TableRow key={pago.id}>
                        <TableCell>{formatPeriodoHumano(pago.periodo)}</TableCell>
                        <TableCell>{pago.fechaPago ? new Date(pago.fechaPago).toLocaleDateString('es-AR') : ''}</TableCell>
                        <TableCell>{formatCurrency(pago.monto)}</TableCell>
                        <TableCell>{pago.metodoPago || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-verde-esmeralda text-white">
                            {pago.estado || 'Pagado'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {comprobante?.data ? (
                            <a
                              href={comprobante.data}
                              download={comprobante.nombre || `comprobante-${pago.periodo}`}
                              className="text-verde-esmeralda underline text-sm flex items-center gap-1"
                            >
                              <FileText className="h-4 w-4" /> Descargar
                            </a>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {selectedGuarderia?.periodosPendientes?.length > 0 && (
            <div className="rounded-lg border border-amarillo-dorado/40 bg-amarillo-dorado/10 p-4">
              <div className="flex items-center gap-2 text-azul-oscuro mb-2">
                <CalendarClock className="h-5 w-5" />
                <p className="font-semibold">Periodos pendientes</p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {selectedGuarderia.periodosPendientes.map((periodo) => (
                  <div key={periodo.periodo} className="rounded-md border border-amarillo-dorado/40 bg-white px-3 py-2">
                    <p className="font-medium text-azul-oscuro">{formatPeriodoHumano(periodo.periodo)}</p>
                    <p className="text-xs text-slate-600">
                      Vence el {periodo.fechaVencimiento ? new Date(periodo.fechaVencimiento).toLocaleDateString('es-AR') : '—'}
                    </p>
                    <p className="text-sm text-azul-oscuro font-semibold">
                      {formatCurrency(periodo.monto)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
            <div className="text-sm text-slate-600">
              Total abonado registrado: <span className="font-semibold text-azul-oscuro">{formatCurrency(totalPagado)}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex items-center gap-2" onClick={handleDownloadResumen}>
                <Download className="h-4 w-4" /> Descargar resumen (CSV)
              </Button>
              <Button variant="outline" onClick={handleCloseHistory}>
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isPaymentOpen} onOpenChange={handlePaymentDialogChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrar pago mensual</DialogTitle>
            <p className="text-sm text-slate-600">
              {selectedGuarderia
                ? `${selectedGuarderia?.cliente?.nombre || ''} ${selectedGuarderia?.cliente?.apellido || ''} · ${selectedGuarderia?.embarcacion?.numeroMatricula || ''}`
                : 'Ingresá los datos del pago'}
            </p>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleRegisterPayment}>
            {selectedGuarderia?.periodosPendientes?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-azul-oscuro mb-1">Periodos adeudados</p>
                <p className="text-xs text-slate-600">
                  Seleccioná los periodos que querés registrar. Los vencidos aparecen resaltados en rojo.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {selectedGuarderia.periodosPendientes.map((periodo) => {
                    const isSelected = paymentForm.periodos.includes(periodo.periodo);
                    const vencido = isPeriodoVencido(periodo);
                    const fechaVencimiento = periodo.fechaVencimiento
                      ? new Date(periodo.fechaVencimiento)
                      : null;
                    const fechaValida = fechaVencimiento && !Number.isNaN(fechaVencimiento.getTime());
                    return (
                      <button
                        type="button"
                        key={periodo.periodo}
                        onClick={() => handleTogglePeriodoPendiente(periodo.periodo)}
                        className={`w-full rounded-lg border px-3 py-2 text-left text-sm shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 ${
                          vencido
                            ? 'border-red-500/70 bg-red-50 text-red-700 hover:bg-red-100 focus:ring-red-500/60'
                            : 'border-azul-oscuro/15 bg-white text-azul-oscuro hover:bg-azul-oscuro/5 focus:ring-verde-esmeralda/40'
                        } ${
                          isSelected
                            ? vencido
                              ? 'border-red-600 bg-red-100'
                              : 'border-verde-esmeralda/60 bg-verde-esmeralda/10'
                            : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-semibold">
                            {formatPeriodoHumano(periodo.periodo)}
                          </span>
                          {isSelected && (
                            <span
                              className={`text-xs font-semibold uppercase tracking-wide ${
                                vencido ? 'text-red-600' : 'text-verde-esmeralda'
                              }`}
                            >
                              Seleccionado
                            </span>
                          )}
                        </div>
                        <p className={`mt-1 text-xs ${vencido ? 'text-red-600' : 'text-slate-600'}`}>
                          {vencido ? 'Vencido' : 'Vence'}
                          {fechaValida ? ` el ${fechaVencimiento.toLocaleDateString('es-AR')}` : ''}
                        </p>
                        <p className={`mt-2 text-sm font-semibold ${vencido ? 'text-red-700' : 'text-azul-oscuro'}`}>
                          {formatCurrency(periodo.monto)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-azul-oscuro mb-2">
                Agregar periodo manualmente (YYYY-MM)
              </label>
              <div className="flex gap-2">
                <Input
                  type="month"
                  value={paymentForm.periodInput}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, periodInput: e.target.value }))}
                />
                <Button type="button" variant="secondary" onClick={handleAddPeriodo}>
                  Agregar
                </Button>
              </div>
              {paymentForm.periodos.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {paymentForm.periodos.map((periodo) => (
                    <span
                      key={periodo}
                      className="inline-flex items-center gap-1 rounded-full bg-verde-esmeralda/10 text-verde-esmeralda border border-verde-esmeralda/40 px-3 py-1 text-sm"
                    >
                      {formatPeriodoHumano(periodo)}
                      <button type="button" onClick={() => handleRemovePeriodo(periodo)} aria-label="Quitar periodo">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-azul-oscuro mb-2">Fecha de pago</label>
                <Input
                  type="date"
                  value={paymentForm.fechaPago}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, fechaPago: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-azul-oscuro mb-2">Monto</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={paymentForm.monto}
                  onChange={handleMontoPagoChange}
                />
                {montoSugerido && !paymentForm.montoEsPersonalizado && (
                  <p className="text-xs text-slate-600 mt-1">
                    Sugerido: {formatCurrency(montoSugerido)}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-azul-oscuro mb-2">Método de pago</label>
              <Select
                value={paymentForm.metodoPago}
                onValueChange={(value) => setPaymentForm((prev) => ({ ...prev, metodoPago: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccioná un método" />
                </SelectTrigger>
                <SelectContent>
                  {METODOS_PAGO.map((metodo) => (
                    <SelectItem key={metodo} value={metodo}>
                      {metodo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-azul-oscuro mb-2">Observaciones</label>
              <textarea
                className="w-full rounded-md border border-azul-oscuro/20 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-verde-esmeralda"
                rows={3}
                value={paymentForm.observaciones}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, observaciones: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-azul-oscuro mb-2">Comprobante (opcional)</label>
              <Input type="file" accept="image/*,application/pdf" onChange={handleComprobanteUpload} />
              {paymentForm.comprobante && (
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                  <FileText className="h-4 w-4" /> {paymentForm.comprobante.nombre}
                  <button
                    type="button"
                    onClick={clearComprobante}
                    className="text-red-600 hover:underline"
                  >
                    Quitar
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleClosePayment} disabled={submittingPayment}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-verde-esmeralda text-white" disabled={submittingPayment}>
                {submittingPayment ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrar pago'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Guarderia;
